import { useState, useMemo } from 'react';
import {
  loadPositions, calcCushion, getStatus,
  loadReviews, saveReviews, generateId,
} from './storage';
import './WeeklyReview.css';

/* ── DCA helpers (mirrored from ETF.js) ── */
const TIERS = [
  { label: 'Within 15% of ATH', min: 0,  max: 15,      pct: 20 },
  { label: '15–25% below ATH',  min: 15, max: 25,      pct: 40 },
  { label: '25–35% below ATH',  min: 25, max: 35,      pct: 60 },
  { label: '35%+ below ATH',    min: 35, max: Infinity, pct: 80 },
];
const getTier = d => TIERS.find(t => d >= t.min && d < t.max) ?? TIERS[0];
const athDist  = (cur, ath) => (!cur || !ath || ath <= 0) ? null : Math.max(0, ((ath - cur) / ath) * 100);

const QUARTERLY_MONTHS = [1, 4, 7, 10]; // Jan, Apr, Jul, Oct
const LEV_NAMES = ['TQQQ', 'UPRO'];
const DEF_NAMES = ['SMIA', 'SCHD'];
const CRYPTO_ASSET = 'Crypto';

/* ── Load FM stocks from localStorage directly ── */
function loadFmStocks() {
  try { return JSON.parse(localStorage.getItem('isa_fm_stocks')) ?? []; }
  catch { return []; }
}
function loadDcaInputs() {
  try { return JSON.parse(localStorage.getItem('isa_dca_inputs')) ?? {}; }
  catch { return {}; }
}

/* ─────────────────────────────────────────────
   Checklist generator
   ───────────────────────────────────────────── */
function generateItems(positions, fmStocks, dcaInputs) {
  const items = [];

  /* 1 ── Trail stop candidates (20%+ gain from entry) */
  positions.forEach(p => {
    if (!p.entryPrice || !p.currentPrice || p.entryPrice <= 0) return;
    const gainPct = ((p.currentPrice - p.entryPrice) / p.entryPrice) * 100;
    if (gainPct < 20) return;
    const cushionPct    = calcCushion(p.currentPrice, p.stopPrice);
    // Scale stop proportionally: new_stop = current * (old_stop / entry)
    const suggestedStop = Math.max(p.stopPrice, p.currentPrice * (p.stopPrice / p.entryPrice));
    items.push({ section: 'trail', id: `trail-${p.id}`, p, gainPct, cushionPct, suggestedStop });
  });

  /* 2 ── Risk alerts */
  // RED positions
  positions.forEach(p => {
    const cushion = calcCushion(p.currentPrice, p.stopPrice);
    if (getStatus(cushion) === 'RED') {
      items.push({ section: 'risk', type: 'red', id: `risk-red-${p.id}`, p, cushion });
    }
  });

  // Crypto allocation > 15%
  const totalVal  = positions.reduce((s, p) => s + p.shares * p.currentPrice, 0);
  const cryptoVal = positions.filter(p => p.assetClass === CRYPTO_ASSET)
                             .reduce((s, p) => s + p.shares * p.currentPrice, 0);
  const cryptoPct = totalVal > 0 ? (cryptoVal / totalVal) * 100 : 0;
  if (cryptoPct > 15) {
    items.push({ section: 'risk', type: 'crypto', id: 'risk-crypto', cryptoPct, cryptoVal, totalVal });
  }

  // Leverage ratio outside 75–85%
  const levVal  = positions.filter(p => LEV_NAMES.includes(p.name.toUpperCase())).reduce((s, p) => s + p.shares * p.currentPrice, 0);
  const defVal  = positions.filter(p => DEF_NAMES.includes(p.name.toUpperCase())).reduce((s, p) => s + p.shares * p.currentPrice, 0);
  const levBase = levVal + defVal;
  if (levBase > 0) {
    const levPct = (levVal / levBase) * 100;
    if (levPct < 75 || levPct > 85) {
      items.push({ section: 'risk', type: 'leverage', id: 'risk-leverage', levPct, defPct: 100 - levPct });
    }
  }

  /* 3 ── DCA reminder (quarterly months only) */
  const month = new Date().getMonth() + 1;
  if (QUARTERLY_MONTHS.includes(month)) {
    const budget    = Number(dcaInputs.budget) || 2000;
    const half      = budget / 2;
    const tqqqDist  = athDist(Number(dcaInputs.tqqqPrice), Number(dcaInputs.tqqqAth));
    const uproDist  = athDist(Number(dcaInputs.uproPrice), Number(dcaInputs.uproAth));
    const tqqqTier  = tqqqDist !== null ? getTier(tqqqDist) : null;
    const uproTier  = uproDist !== null ? getTier(uproDist) : null;
    items.push({
      section:   'dca',
      id:        'dca-reminder',
      monthName: new Date().toLocaleString('default', { month: 'long' }),
      budget,
      tqqqDist, tqqqTier, tqqqAlloc: tqqqTier ? (half * tqqqTier.pct / 100) : null,
      uproDist, uproTier, uproAlloc: uproTier ? (half * uproTier.pct / 100) : null,
    });
  }

  /* 4 ── Future Markets near-entry stocks */
  fmStocks
    .filter(s => s.stage === 'WATCHING' && s.nearEntry)
    .forEach(s => items.push({ section: 'future', id: `fm-${s.id}`, stock: s }));

  /* 5 ── Manual reminders (always present) */
  items.push({ section: 'manual', id: 'manual-ibkr',        text: 'Verify all GTC orders are active on IBKR' });
  items.push({ section: 'manual', id: 'manual-swissquote',  text: 'Verify all stop orders are active on Swissquote' });

  return items;
}

/* ─────────────────────────────────────────────
   Main component
   ───────────────────────────────────────────── */
export default function WeeklyReview({ onClose }) {
  const positions  = useMemo(loadPositions,  []);
  const fmStocks   = useMemo(loadFmStocks,   []);
  const dcaInputs  = useMemo(loadDcaInputs,  []);
  const items      = useMemo(() => generateItems(positions, fmStocks, dcaInputs), [positions, fmStocks, dcaInputs]);

  const [checked,  setChecked]  = useState(new Set());
  const [saved,    setSaved]    = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [reviews,  setReviews]  = useState(loadReviews);

  function toggle(id) {
    setChecked(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function handleSave() {
    const review = {
      id:           generateId(),
      date:         new Date().toISOString(),
      totalItems:   items.length,
      checkedItems: [...checked],
    };
    const updated = [review, ...reviews].slice(0, 52); // keep 1 year of weekly reviews
    setReviews(updated);
    saveReviews(updated);
    setSaved(true);
  }

  const total     = items.length;
  const doneCount = checked.size;
  const progress  = total > 0 ? (doneCount / total) * 100 : 0;

  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  const sections = {
    trail:  items.filter(i => i.section === 'trail'),
    risk:   items.filter(i => i.section === 'risk'),
    dca:    items.filter(i => i.section === 'dca'),
    future: items.filter(i => i.section === 'future'),
    manual: items.filter(i => i.section === 'manual'),
  };

  return (
    <div className="modal-overlay wr-overlay" onClick={onClose}>
      <div className="modal-card wr-card" onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="wr-header">
          <div>
            <h2 className="wr-title">📋 Weekly Review</h2>
            <p className="wr-date">{today}</p>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* ── Progress bar ── */}
        <div className="wr-progress-wrap">
          <div className="wr-progress-bar" style={{ width: `${progress}%` }} />
          <span className="wr-progress-label">{doneCount} / {total} completed</span>
        </div>

        {/* ── Checklist body ── */}
        <div className="wr-body">

          {/* 1 · Trail Stop Candidates */}
          <Section title="Trail Stop Candidates" icon="📈" count={sections.trail.length} empty="No positions with 20%+ gain from entry.">
            {sections.trail.map(item => (
              <CheckItem key={item.id} id={item.id} checked={checked.has(item.id)} onToggle={toggle}>
                <div className="ci-trail">
                  <span className="ci-ticker">{item.p.name}</span>
                  <span className="ci-gain gain--positive">+{item.gainPct.toFixed(1)}%</span>
                  <span className="ci-detail">
                    Stop: <strong>${item.p.stopPrice.toFixed(2)}</strong>
                    {item.suggestedStop > item.p.stopPrice && (
                      <> → suggest <strong className="ci-suggest">${item.suggestedStop.toFixed(2)}</strong></>
                    )}
                    <span className={`ci-cushion cushion-inline cushion--${getStatus(item.cushionPct).toLowerCase()}`}>
                      {' '}({item.cushionPct.toFixed(1)}% cushion)
                    </span>
                  </span>
                </div>
              </CheckItem>
            ))}
          </Section>

          {/* 2 · Risk Alerts */}
          <Section title="Risk Alerts" icon="⚠" count={sections.risk.length} empty="No risk alerts — portfolio looks healthy.">
            {sections.risk.map(item => (
              <CheckItem key={item.id} id={item.id} checked={checked.has(item.id)} onToggle={toggle}>
                {item.type === 'red' && (
                  <div className="ci-risk ci-risk--red">
                    <span className="ci-ticker">{item.p.name}</span>
                    <span className="ci-detail">
                      RED — only <strong>{item.cushion.toFixed(1)}%</strong> cushion above stop-loss
                    </span>
                  </div>
                )}
                {item.type === 'crypto' && (
                  <div className="ci-risk ci-risk--amber">
                    <span>🪙 Crypto allocation</span>
                    <span className="ci-detail">
                      <strong>{item.cryptoPct.toFixed(1)}%</strong> of portfolio (limit: 15%)
                    </span>
                  </div>
                )}
                {item.type === 'leverage' && (
                  <div className="ci-risk ci-risk--amber">
                    <span>⚖ Leverage ratio</span>
                    <span className="ci-detail">
                      Currently <strong>{item.levPct.toFixed(1)}:{item.defPct.toFixed(1)}</strong> (target 80:20, range 75–85)
                    </span>
                  </div>
                )}
              </CheckItem>
            ))}
          </Section>

          {/* 3 · DCA Reminder */}
          <Section
            title="DCA Reminder"
            icon="💰"
            count={sections.dca.length}
            empty={`No DCA due this month. Next quarterly month: ${nextQuarterMonth()}.`}
          >
            {sections.dca.map(item => (
              <CheckItem key={item.id} id={item.id} checked={checked.has(item.id)} onToggle={toggle}>
                <div className="ci-dca">
                  <span className="ci-dca-month">Q month — {item.monthName}</span>
                  <div className="ci-dca-rows">
                    <DcaRow ticker="TQQQ" dist={item.tqqqDist} tier={item.tqqqTier} alloc={item.tqqqAlloc} />
                    <DcaRow ticker="UPRO" dist={item.uproDist} tier={item.uproTier} alloc={item.uproAlloc} />
                  </div>
                  <div className="ci-dca-total">
                    Total to invest: <strong>
                      ${((item.tqqqAlloc ?? 0) + (item.uproAlloc ?? 0)).toFixed(0)}
                    </strong> of ${item.budget} budget
                    {(!item.tqqqTier || !item.uproTier) && (
                      <span className="ci-dca-hint"> — update prices in the ETF tab</span>
                    )}
                  </div>
                </div>
              </CheckItem>
            ))}
          </Section>

          {/* 4 · Future Markets */}
          <Section
            title="Future Markets Watch"
            icon="🚀"
            count={sections.future.length}
            empty="No WATCHING stocks flagged as near entry. Use the 📌 button on the Future tab."
          >
            {sections.future.map(item => (
              <CheckItem key={item.id} id={item.id} checked={checked.has(item.id)} onToggle={toggle}>
                <div className="ci-future">
                  <span className="ci-ticker">{item.stock.ticker}</span>
                  <span className="ci-company">{item.stock.company}</span>
                  {item.stock.entryCondition && (
                    <span className="ci-detail ci-entry-cond">"{item.stock.entryCondition}"</span>
                  )}
                </div>
              </CheckItem>
            ))}
          </Section>

          {/* 5 · Manual Reminders */}
          <Section title="Manual Checks" icon="✅" count={sections.manual.length}>
            {sections.manual.map(item => (
              <CheckItem key={item.id} id={item.id} checked={checked.has(item.id)} onToggle={toggle}>
                <span className="ci-manual">{item.text}</span>
              </CheckItem>
            ))}
          </Section>

        </div>

        {/* ── Footer ── */}
        <div className="wr-footer">
          <button
            className="btn btn-ghost wr-history-btn"
            onClick={() => setShowHistory(h => !h)}
          >
            {showHistory ? 'Hide History' : `History (${reviews.length})`}
          </button>
          {saved ? (
            <span className="wr-saved-msg">✓ Review saved</span>
          ) : (
            <button className="btn btn-primary" onClick={handleSave}>
              Save Review
            </button>
          )}
        </div>

        {/* ── Review history panel ── */}
        {showHistory && (
          <div className="wr-history">
            <h3 className="wr-history-title">Past Reviews</h3>
            {reviews.length === 0 ? (
              <p className="wr-history-empty">No saved reviews yet.</p>
            ) : (
              reviews.map(r => {
                const pct = r.totalItems > 0 ? Math.round((r.checkedItems.length / r.totalItems) * 100) : 0;
                return (
                  <div key={r.id} className="wr-history-row">
                    <span className="wr-h-date">
                      {new Date(r.date).toLocaleDateString(undefined, {
                        weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
                      })}
                    </span>
                    <div className="wr-h-bar-wrap">
                      <div className="wr-h-bar" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="wr-h-pct">{r.checkedItems.length}/{r.totalItems}</span>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Helpers ─── */
function nextQuarterMonth() {
  const m = new Date().getMonth() + 1;
  const next = QUARTERLY_MONTHS.find(qm => qm > m) ?? QUARTERLY_MONTHS[0];
  return new Date(0, next - 1).toLocaleString('default', { month: 'long' });
}

function Section({ title, icon, count, empty, children }) {
  return (
    <div className="wr-section">
      <div className="wr-section-header">
        <span className="wr-section-icon">{icon}</span>
        <span className="wr-section-title">{title}</span>
        <span className="wr-section-count">{count}</span>
      </div>
      {count === 0 && empty ? (
        <p className="wr-section-empty">{empty}</p>
      ) : (
        <div className="wr-section-items">{children}</div>
      )}
    </div>
  );
}

function CheckItem({ id, checked, onToggle, children }) {
  return (
    <label className={`check-item${checked ? ' check-item--done' : ''}`}>
      <input
        type="checkbox"
        className="check-item-box"
        checked={checked}
        onChange={() => onToggle(id)}
      />
      <span className="check-item-content">{children}</span>
    </label>
  );
}

function DcaRow({ ticker, dist, tier, alloc }) {
  if (!tier) return (
    <div className="dca-row dca-row--missing">
      <span className="dca-row-ticker">{ticker}</span>
      <span className="dca-row-hint">— enter prices in ETF tab</span>
    </div>
  );
  return (
    <div className="dca-row">
      <span className="dca-row-ticker">{ticker}</span>
      <span className="dca-row-dist">{dist.toFixed(1)}% below ATH</span>
      <span className="dca-row-tier">{tier.label} → invest {tier.pct}%</span>
      <span className="dca-row-alloc">${alloc.toFixed(0)}</span>
    </div>
  );
}
